import { computed, reactive, readonly, toValue, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useAccountResource } from 'dashboard/composables/useAccountResource';

const state = reactive({
  accountId: null,
  conversationId: null,
  first: null,
  last: null,
  target: 'first',
  choice: null,
  messages: [],
  resolving: false,
  rangeError: '',
  locked: false,
  mode: 'followup',
  supportTicketId: null,
  closingSupport: false,
});

const compareMessages = (first, last) =>
  first.createdAt - last.createdAt || first.id - last.id;

const isEligible = message =>
  Number.isInteger(message.id) &&
  !message.private &&
  [0, 1].includes(message.messageType) &&
  Boolean(message.content) &&
  !message.contentAttributes?.deleted;

const reset = () => {
  state.first = null;
  state.last = null;
  state.target = 'first';
  state.choice = null;
  state.messages = [];
  state.resolving = false;
  state.rangeError = '';
};

const close = () => {
  state.conversationId = null;
  state.accountId = null;
  state.locked = false;
  state.mode = 'followup';
  state.supportTicketId = null;
  state.closingSupport = false;
  reset();
};

const isWithinRange = message => {
  const first = state.first || state.last;
  if (!first || !isEligible(message)) return false;
  if (!state.resolving && !state.rangeError && state.messages.length) {
    return state.messages.some(point => point.id === message.id);
  }
  const last = state.last || first;
  return (
    compareMessages(message, first) >= 0 && compareMessages(message, last) <= 0
  );
};

const setRange = (first, last) => {
  [state.first, state.last] =
    compareMessages(first, last) > 0 ? [last, first] : [first, last];
  state.target = null;
  state.choice = null;
  state.messages = [];
  state.rangeError = '';
  state.resolving = true;
};

const chooseBoundary = target => {
  if (state.locked || state.resolving || !state.choice) return;
  setRange(
    target === 'first' ? state.choice : state.first,
    target === 'last' ? state.choice : state.last
  );
};

const selectMessage = message => {
  if (
    state.locked ||
    (state.resolving && state.first?.id !== state.last?.id) ||
    state.conversationId !== message.conversationId ||
    !isEligible(message)
  ) {
    return;
  }
  const point = {
    id: message.id,
    createdAt: message.createdAt,
    text: message.content,
    author: message.sender?.name || '',
  };
  if (!state.first) {
    setRange(point, point);
    return;
  }
  if (state.target) {
    setRange(
      state.target === 'first' ? point : state.first,
      state.target === 'last' ? point : state.last
    );
    return;
  }
  const firstSelected = state.first.id === point.id;
  const lastSelected = state.last.id === point.id;
  if (firstSelected && lastSelected) {
    reset();
    return;
  }
  if (firstSelected || lastSelected) {
    if (state.messages.length < 2) return;
    setRange(
      firstSelected ? state.messages[1] : state.first,
      lastSelected ? state.messages[state.messages.length - 2] : state.last
    );
    return;
  }
  if (isWithinRange(message)) {
    state.choice = point;
    return;
  }
  setRange(
    compareMessages(point, state.first) < 0 ? point : state.first,
    compareMessages(point, state.last) > 0 ? point : state.last
  );
};

export function useGgFollowupSelection() {
  return {
    selection: readonly(state),
    close,
    reset,
    isEligible,
    isWithinRange,
    selectMessage,
    chooseBoundary,
    clearChoice: () => {
      state.choice = null;
    },
    resolveRange: (messages, error = '') => {
      state.messages = messages;
      state.rangeError = error;
      state.resolving = false;
    },
    setResolving: resolving => {
      state.resolving = resolving;
      state.rangeError = '';
    },
    setTarget: target => {
      if (state.locked || state.resolving) return;
      state.target = state.target === target ? null : target;
      state.choice = null;
    },
    setLocked: locked => {
      state.locked = locked;
    },
  };
}

export function useGgFollowup(mode = 'followup', contextConversationId = null) {
  const route = useRoute();
  const router = useRouter();
  const accountId = useMapGetter('getCurrentAccountId');
  const currentChat = useMapGetter('getSelectedChat');
  const inboxId = computed(() =>
    contextConversationId === null ||
    currentChat.value.id === toValue(contextConversationId)
      ? currentChat.value.inbox_id
      : null
  );
  const accountResourceEnabled = useAccountResource(
    mode === 'support' ? 'gg_support' : 'gg_followup',
    mode === 'support' ? inboxId : undefined
  );
  const enabled = computed(
    () =>
      accountResourceEnabled.value &&
      (mode === 'support'
        ? window.chatwootConfig?.ggSupportEnabled
        : window.chatwootConfig?.ggFollowupEnabled) &&
      String(accountId.value) === window.chatwootConfig.ggFollowupAccountId
  );
  watch(enabled, allowed => {
    if (
      !allowed &&
      state.accountId === Number(accountId.value) &&
      state.mode === mode
    ) {
      close();
    }
  });
  const open = async (
    conversationId,
    { supportTicketId = null, closingSupport = false } = {}
  ) => {
    if (!enabled.value) return;
    const requestedAccountId = Number(accountId.value);
    if (Number(route.params.conversation_id) !== conversationId) {
      await router.push(
        frontendURL(
          `accounts/${requestedAccountId}/conversations/${conversationId}`
        )
      );
    }
    reset();
    state.accountId = requestedAccountId;
    state.conversationId = conversationId;
    state.locked = false;
    state.mode = mode;
    state.supportTicketId = supportTicketId;
    state.closingSupport = closingSupport;
  };
  return { enabled, open };
}
